import React, { useState, useEffect } from 'react';
import { apiClient } from './apiClient';
import { Search, Plus, ChevronLeft, AlertTriangle, Power, Wrench, Users, Monitor, DollarSign, Calculator } from 'lucide-react';

const App = () => {
  // Estados principales
  const [vista, setVista] = useState('dashboard');
  const [clientes, setClientes] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [clientesFrecuentesIds, setClientesFrecuentesIds] = useState([]);
  const [diaActual, setDiaActual] = useState('');
  const [historialDias, setHistorialDias] = useState([]);

  // Estados de selección y UI
  const [diaSeleccionado, setDiaSeleccionado] = useState(null);
  const [equipoSeleccionado, setEquipoSeleccionado] = useState(null);
  const [clienteEditando, setClienteEditando] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [busquedaClientes, setBusquedaClientes] = useState('');
  const [mostrarConfirmacionCerrarDia, setMostrarConfirmacionCerrarDia] = useState(false);
  const [mostrarHistorialEquipos, setMostrarHistorialEquipos] = useState(false);
  const [imagenModalUrl, setImagenModalUrl] = useState(null);
  const [cargando, setCargando] = useState(false); // Nuevo estado para feedback visual

  // Estados para edición
  const [precioTemp, setPrecioTemp] = useState('');
  const [costoTemp, setCostoTemp] = useState('');
  const [nombreEdit, setNombreEdit] = useState('');
  const [cedulaEdit, setCedulaEdit] = useState('');
  const [contactoEdit, setContactoEdit] = useState('');

  // Formulario nuevo equipo
  const [nuevoEquipo, setNuevoEquipo] = useState({
    clienteId: '',
    clienteNombre: '',
    clienteCedula: '',
    clienteContacto: '',
    foto: null,
    detalle: '',
    estado: 'apagado',
    riesgo: false,
    precioReparacion: '',
    costoRepuesto: '',
    tecnico: '',
    credito: false,
    deudaSaldada: false
  });

  // Formulario nuevo cliente frecuente
  const [nuevoClienteFrecuente, setNuevoClienteFrecuente] = useState({
    nombre: '',
    cedula: '',
    contacto: ''
  });

  // CARGA INICIAL
  useEffect(() => {
    cargarDatos();
  }, []);

  // Efectos para rellenar formularios de edición
  useEffect(() => {
    if (vista === 'editarCostos' && equipoSeleccionado) {
      setPrecioTemp(equipoSeleccionado.precioReparacion || '');
      setCostoTemp(equipoSeleccionado.costoRepuesto || '');
    }
  }, [vista, equipoSeleccionado]);

  useEffect(() => {
    if (vista === 'editarCliente' && clienteEditando) {
      setNombreEdit(clienteEditando.nombre || '');
      setCedulaEdit(clienteEditando.cedula || '');
      setContactoEdit(clienteEditando.contacto || '');
    }
  }, [vista, clienteEditando]);

  // ==========================================
  // FUNCIONES DE CONEXIÓN A LA BASE DE DATOS
  // ==========================================

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const data = await apiClient.syncAll();
      if (data.success) {
        setClientes(data.clientes);
        setEquipos(data.equipos);
        // Filtramos los que tienen esFrecuente = true
        setClientesFrecuentesIds(data.clientes.filter(c => c.esFrecuente).map(c => c.id));
        setDiaActual(data.diaActual || new Date().toLocaleDateString('es-EC'));
        setHistorialDias(data.dias);
      }
    } catch (error) {
      console.error("Error cargando datos:", error);
    } finally {
      setCargando(false);
    }
  };

  const cerrarDia = () => {
    if (equipos.length === 0) return;
    setMostrarConfirmacionCerrarDia(true);
  };

  const confirmarCerrarDia = async () => {
    // 1. Calcular totales de equipos cerrados (no crédito o pagados)
    const equiposParaCerrar = equipos.filter(e => !e.credito || e.deudaSaldada);
    const totalIngresos = equiposParaCerrar.reduce((sum, e) => sum + parseFloat(e.precioReparacion || 0), 0);
    const totalRepuestos = equiposParaCerrar.reduce((sum, e) => sum + parseFloat(e.costoRepuesto || 0), 0);

    const registroDia = {
      fecha: diaActual,
      equipos: [...equiposParaCerrar],
      totalIngresos,
      totalRepuestos,
      gananciaTotal: totalIngresos - totalRepuestos
    };

    try {
      // 2. Enviar a la base de datos
      await apiClient.createDiaCerrado(registroDia);

      // 3. Actualizar fecha en DB (opcional, para reiniciar día)
      // await apiClient.updateDiaActual(new Date().toLocaleDateString('es-EC')); 

      await cargarDatos(); // Recargar todo
      setMostrarConfirmacionCerrarDia(false);
      setVista('dashboard');
      alert("Día cerrado correctamente.");
    } catch (error) {
      alert("Error al cerrar el día: " + error.message);
    }
  };

  const guardarEquipo = async () => {
    if (!nuevoEquipo.clienteNombre || !nuevoEquipo.detalle) {
      alert('Por favor completa los campos obligatorios');
      return;
    }

    const equipo = {
      id: Date.now().toString(),
      ...nuevoEquipo,
      fecha: new Date().toLocaleDateString('es-EC'),
      hora: new Date().toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })
    };

    setCargando(true);
    try {
      // Si es cliente nuevo (no tiene ID seleccionado), crearlo primero
      if (!nuevoEquipo.clienteId) {
        const clienteNuevo = {
          id: Date.now().toString(),
          nombre: nuevoEquipo.clienteNombre,
          cedula: nuevoEquipo.clienteCedula,
          contacto: nuevoEquipo.clienteContacto,
          esFrecuente: false
        };
        await apiClient.createCliente(clienteNuevo);
        equipo.clienteId = clienteNuevo.id;
      }

      // Guardar equipo en Cloudflare
      await apiClient.createEquipo(equipo);

      // Limpiar y recargar
      await cargarDatos();
      setNuevoEquipo({
        clienteId: '', clienteNombre: '', clienteCedula: '', clienteContacto: '',
        foto: null, detalle: '', estado: 'apagado', riesgo: false,
        precioReparacion: '', costoRepuesto: '', tecnico: '',
        credito: false, deudaSaldada: false
      });
      setVista('dashboard');
    } catch (error) {
      alert("Error guardando equipo: " + error.message);
    } finally {
      setCargando(false);
    }
  };

  const guardarCambiosCostos = async () => {
    if (!equipoSeleccionado) return;

    setCargando(true);
    try {
      await apiClient.updateEquipo(equipoSeleccionado.id, {
        precioReparacion: precioTemp,
        costoRepuesto: costoTemp
      });
      await cargarDatos();
      setVista('contabilidad');
    } catch (error) {
      alert("Error actualizando costos: " + error.message);
    } finally {
      setCargando(false);
    }
  };

  const marcarDeudaSaldada = async (equipoId) => {
    if (!window.confirm("¿Confirmar que la deuda ha sido pagada?")) return;

    setCargando(true);
    try {
      await apiClient.updateEquipo(equipoId, { deudaSaldada: true });
      await cargarDatos();
    } catch (error) {
      alert("Error al saldar deuda: " + error.message);
    } finally {
      setCargando(false);
    }
  };

  const guardarClienteFrecuente = async () => {
    if (!nuevoClienteFrecuente.nombre || !nuevoClienteFrecuente.cedula || !nuevoClienteFrecuente.contacto) {
      alert('Por favor completa todos los campos');
      return;
    }

    setCargando(true);
    try {
      await apiClient.createCliente({
        id: Date.now().toString(),
        nombre: nuevoClienteFrecuente.nombre,
        cedula: nuevoClienteFrecuente.cedula,
        contacto: nuevoClienteFrecuente.contacto,
        esFrecuente: true
      });

      setNuevoClienteFrecuente({ nombre: '', cedula: '', contacto: '' });
      await cargarDatos();
      alert('Técnico agregado correctamente');
    } catch (error) {
      alert("Error agregando técnico: " + error.message);
    } finally {
      setCargando(false);
    }
  };

  const toggleClienteFrecuente = async (cliente) => {
    // Invertir estado de esFrecuente
    const nuevoEstado = !cliente.esFrecuente;

    try {
      await apiClient.updateCliente(cliente.id, {
        nombre: cliente.nombre,
        cedula: cliente.cedula,
        contacto: cliente.contacto,
        esFrecuente: nuevoEstado
      });
      await cargarDatos();
    } catch (error) {
      console.error("Error cambiando estado de técnico", error);
    }
  };

  const guardarEdicionCliente = async () => {
    if (!nombreEdit || !cedulaEdit || !contactoEdit) {
      alert('Por favor completa todos los campos');
      return;
    }

    setCargando(true);
    try {
      await apiClient.updateCliente(clienteEditando.id, {
        nombre: nombreEdit,
        cedula: cedulaEdit,
        contacto: contactoEdit,
        esFrecuente: clienteEditando.esFrecuente // Mantener estado actual
      });
      await cargarDatos();
      setClienteEditando(null);
      setVista('clientes');
      alert('Cliente actualizado');
    } catch (error) {
      alert("Error actualizando cliente: " + error.message);
    } finally {
      setCargando(false);
    }
  };

  const eliminarCliente = async (clienteId) => {
    if (!window.confirm('¿Estás seguro? También se eliminarán sus equipos registrados.')) return;

    setCargando(true);
    try {
      await apiClient.deleteCliente(clienteId);
      await cargarDatos();
      setClienteEditando(null);
      setVista('clientes');
      alert('Cliente eliminado');
    } catch (error) {
      alert("Error eliminando cliente: " + error.message);
    } finally {
      setCargando(false);
    }
  };

  // ==========================================
  // LÓGICA DE INTERFAZ (FILTROS Y AYUDAS)
  // ==========================================

  const handleImagenCaptura = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const maxWidth = 800;
          const maxHeight = 800;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          const resizedImage = canvas.toDataURL('image/jpeg', 0.8);
          setNuevoEquipo({ ...nuevoEquipo, foto: resizedImage });
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const seleccionarCliente = (cliente) => {
    setNuevoEquipo({
      ...nuevoEquipo,
      clienteId: cliente.id,
      clienteNombre: cliente.nombre,
      clienteCedula: cliente.cedula,
      clienteContacto: cliente.contacto
    });
  };

  const clientesFrecuentes = clientes.filter(c => clientesFrecuentesIds.includes(c.id));

  const equiposFiltrados = equipos.filter(e =>
    e.clienteNombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    e.detalle.toLowerCase().includes(busqueda.toLowerCase()) ||
    (e.clienteCedula && e.clienteCedula.includes(busqueda))
  );

  const todosLosEquiposHistorico = historialDias.flatMap(dia =>
    dia.equipos.map(equipo => ({
      ...equipo,
      fechaDia: dia.fecha
    }))
  );

  const equiposHistoricoFiltrados = todosLosEquiposHistorico.filter(e =>
    e.clienteNombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    e.detalle.toLowerCase().includes(busqueda.toLowerCase()) ||
    (e.clienteCedula && e.clienteCedula.includes(busqueda)) ||
    e.fechaDia.includes(busqueda)
  );

  const equiposHistoricosPorFecha = historialDias.map(dia => ({
    fecha: dia.fecha,
    equipos: dia.equipos.filter(e =>
      e.clienteNombre.toLowerCase().includes(busqueda.toLowerCase()) ||
      e.detalle.toLowerCase().includes(busqueda.toLowerCase()) ||
      (e.clienteCedula && e.clienteCedula.includes(busqueda)) ||
      dia.fecha.includes(busqueda)
    )
  })).filter(dia => dia.equipos.length > 0);

  const clientesFiltrados = clientes.filter(c => {
    const busquedaLower = busquedaClientes.toLowerCase();
    const coincideBasico =
      c.nombre.toLowerCase().includes(busquedaLower) ||
      (c.cedula && c.cedula.includes(busquedaClientes)) ||
      (c.contacto && c.contacto.toLowerCase().includes(busquedaLower));

    const equiposEnCredito = equipos.filter(e =>
      e.clienteId === c.id && e.credito && !e.deudaSaldada
    );
    const tieneDeuda = equiposEnCredito.length > 0;

    const buscarPorDeuda =
      (busquedaLower.includes('deuda') && tieneDeuda) ||
      (busquedaLower.includes('credito') && tieneDeuda) ||
      (busquedaLower.includes('crédito') && tieneDeuda) ||
      (busquedaLower.includes('debe') && tieneDeuda) ||
      (busquedaLower.includes('pendiente') && tieneDeuda);

    return coincideBasico || buscarPorDeuda;
  });

  // ==========================================
  // RENDERIZADO (VIEWS)
  // ==========================================

  if (mostrarConfirmacionCerrarDia) {
    const equiposConCostos = equipos.filter(e => e.precioReparacion || e.costoRepuesto);
    const equiposSinCostos = equipos.length - equiposConCostos.length;
    const totalIngresos = equipos.reduce((sum, e) => sum + parseFloat(e.precioReparacion || 0), 0);
    const totalRepuestos = equipos.reduce((sum, e) => sum + parseFloat(e.costoRepuesto || 0), 0);
    const gananciaTotal = totalIngresos - totalRepuestos;

    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
        <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', maxWidth: '500px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
          <h2 style={{ margin: '0 0 20px 0', color: '#dc2626', fontSize: '24px', fontWeight: 'bold', borderBottom: '2px solid #dc2626', paddingBottom: '10px' }}>
            CONFIRMAR CERRAR DÍA
          </h2>
          <div style={{ margin: '20px 0', color: '#374151', lineHeight: '1.8', fontSize: '15px' }}>
            <div style={{ backgroundColor: '#f3f4f6', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
              <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Resumen del día:</p>
              <p style={{ margin: '5px 0' }}><strong>Total de equipos:</strong> {equipos.length}</p>
              <p style={{ margin: '5px 0' }}><strong>Con costos registrados:</strong> {equiposConCostos.length}</p>
              {equiposSinCostos > 0 && (
                <p style={{ margin: '5px 0', color: '#dc2626' }}><strong>Sin costos:</strong> {equiposSinCostos}</p>
              )}
            </div>
            <div style={{ backgroundColor: '#dcfce7', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '2px solid #16a34a' }}>
              <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#15803d' }}>Totales:</p>
              <p style={{ margin: '5px 0' }}><strong>Ingresos:</strong> ${totalIngresos.toFixed(2)}</p>
              <p style={{ margin: '5px 0' }}><strong>Costos:</strong> ${totalRepuestos.toFixed(2)}</p>
              <p style={{ margin: '10px 0 0 0', fontSize: '18px' }}><strong>GANANCIA:</strong> <span style={{ color: '#16a34a', fontSize: '20px' }}>${gananciaTotal.toFixed(2)}</span></p>
            </div>
            <p style={{ fontSize: '14px', color: '#6b7280', backgroundColor: '#fef3c7', padding: '12px', borderRadius: '6px', border: '1px solid #fbbf24' }}>
              <strong>Advertencia:</strong> Esta acción guardará todos los equipos del día actual y comenzará un nuevo día. No se puede deshacer.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '25px' }}>
            <button onClick={confirmarCerrarDia} style={{ flex: 1, padding: '16px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}>
              SÍ, CERRAR DÍA
            </button>
            <button onClick={() => setMostrarConfirmacionCerrarDia(false)} style={{ flex: 1, padding: '16px', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}>
              CANCELAR
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (imagenModalUrl) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.95)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setImagenModalUrl(null)}>
        <button onClick={() => setImagenModalUrl(null)} style={{ position: 'absolute', top: '20px', right: '20px', backgroundColor: 'rgba(255, 255, 255, 0.9)', color: '#000', border: 'none', borderRadius: '50%', width: '40px', height: '40px', fontSize: '24px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.3)', zIndex: 10000 }}>✕</button>
        <div style={{ maxWidth: '90%', maxHeight: '90%', overflow: 'auto' }}>
          <img src={imagenModalUrl} alt="Full size" style={{ width: '100%', height: 'auto', borderRadius: '8px', cursor: 'zoom-in' }} onClick={(e) => { e.stopPropagation(); }} />
        </div>
      </div>
    );
  }

  // DASHBOARD VIEW
  if (vista === 'dashboard') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white p-6 shadow-xl">
          <div className="text-center">
            <h1 className="text-3xl font-bold tracking-wider mb-1">REPAIRCELL</h1>
            <p className="text-lg font-light tracking-wide">SERVICIO TÉCNICO</p>
            <div style={{ width: '60px', height: '2px', background: 'white', margin: '8px auto 0', opacity: 0.7 }}></div>
          </div>
        </div>
        <div className="p-4 space-y-4">
          {cargando && <div className="text-center p-2 bg-blue-100 text-blue-700 rounded">Sincronizando con la nube...</div>}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Equipos</p>
                  <p className="text-3xl font-bold text-blue-600">{equipos.length}</p>
                  {equipos.filter(e => e.credito && !e.deudaSaldada).length > 0 && (
                    <p className="text-xs text-yellow-600 mt-1">{equipos.filter(e => e.credito && !e.deudaSaldada).length} en crédito</p>
                  )}
                </div>
                <Monitor className="text-blue-600" size={40} />
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Clientes</p>
                  <p className="text-3xl font-bold text-green-600">{clientes.length}</p>
                  {clientes.some(c => equipos.some(e => e.clienteId === c.id && e.credito && !e.deudaSaldada)) && (
                    <p className="text-xs text-yellow-600 mt-1">Con deudas activas</p>
                  )}
                </div>
                <Users className="text-green-600" size={40} />
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <button onClick={() => setVista('nuevo')} className="w-full bg-blue-600 text-white p-4 rounded-lg shadow-lg flex items-center justify-center space-x-2 hover:bg-blue-700"><Plus size={24} /><span className="font-semibold">Registrar Equipo</span></button>
            <button onClick={() => setVista('equipos')} className="w-full bg-white text-gray-700 p-4 rounded-lg shadow flex items-center justify-center space-x-2 hover:bg-gray-50"><Monitor size={24} /><span className="font-semibold">Ver Equipos</span></button>
            <button onClick={() => setVista('clientes')} className="w-full bg-white text-gray-700 p-4 rounded-lg shadow flex items-center justify-center space-x-2 hover:bg-gray-50"><Users size={24} /><span className="font-semibold">Ver Clientes</span></button>
            <button onClick={() => setVista('frecuentes')} className="w-full bg-orange-600 text-white p-4 rounded-lg shadow flex items-center justify-center space-x-2 hover:bg-orange-700"><Users size={24} /><span className="font-semibold">Técnicos</span></button>
            <button onClick={() => setVista('contabilidad')} className="w-full bg-green-600 text-white p-4 rounded-lg shadow flex items-center justify-center space-x-2 hover:bg-green-700"><Calculator size={24} /><span className="font-semibold">Contabilidad</span></button>
          </div>
          {equipos.length > 0 && (
            <>
              {equipos.every(e => e.credito && !e.deudaSaldada) && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg shadow">
                  <div className="flex items-start">
                    <AlertTriangle className="text-yellow-600 mt-1 mr-3 flex-shrink-0" size={24} />
                    <div><h3 className="font-semibold text-yellow-800 mb-1">Equipos Pendientes de Pago</h3><p className="text-sm text-yellow-700">Todos los equipos están pendientes de cobro.</p></div>
                  </div>
                </div>
              )}
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="font-semibold text-gray-700 mb-3">Últimos Ingresos</h3>
                <div className="space-y-2">
                  {equipos.slice(-3).reverse().map(equipo => (
                    <div key={equipo.id} className="border-l-4 border-blue-600 pl-3 py-2">
                      <div className="flex items-center justify-between">
                        <div className="flex-1"><p className="font-medium">{equipo.clienteNombre}</p><p className="text-sm text-gray-600">{equipo.detalle.substring(0, 40)}...</p><p className="text-xs text-gray-400">{equipo.fecha} - {equipo.hora}</p></div>
                        {equipo.credito && !equipo.deudaSaldada && (<span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full font-semibold">Crédito</span>)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // EQUIPOS VIEW
  if (vista === 'equipos') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-blue-600 text-white p-4 shadow-lg">
          <div className="flex items-center space-x-3">
            <button onClick={() => setVista('dashboard')}><ChevronLeft size={24} /></button>
            <h2 className="text-xl font-bold">Equipos en Servicio</h2>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <input type="text" placeholder="Buscar..." value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center justify-between">
              <span>Equipos del Día Actual</span>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">{equiposFiltrados.length} equipos</span>
            </h3>
            {equiposFiltrados.length === 0 ? (
              <div className="text-center py-8 text-gray-500"><Monitor size={48} className="mx-auto mb-3 opacity-50" /><p>No hay equipos hoy</p></div>
            ) : (
              <div className="space-y-3">
                {equiposFiltrados.map(equipo => (
                  <div key={equipo.id} onClick={() => { setEquipoSeleccionado(equipo); setVista('detalle'); }} className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition">
                    <div className="flex space-x-3">
                      {equipo.foto && (
                        <div className="relative cursor-pointer group flex-shrink-0" onClick={(e) => { e.stopPropagation(); setImagenModalUrl(equipo.foto); }}>
                          <img src={equipo.foto} alt="Equipo" className="w-20 h-20 object-cover rounded" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1"><h3 className="font-semibold text-gray-800">{equipo.clienteNombre}</h3>{equipo.riesgo && <AlertTriangle className="text-red-500" size={20} />}</div>
                        <p className="text-sm text-gray-600 mb-2">{equipo.detalle}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 text-xs text-gray-500">
                            <span>{equipo.fecha}</span>
                            <span className="capitalize">{equipo.estado}</span>
                          </div>
                          {(equipo.precioReparacion || equipo.costoRepuesto) && <div className="text-xs font-semibold text-green-600">${(parseFloat(equipo.precioReparacion || 0) - parseFloat(equipo.costoRepuesto || 0)).toFixed(2)}</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => setMostrarHistorialEquipos(!mostrarHistorialEquipos)} className="w-full bg-gray-600 text-white p-3 rounded-lg font-semibold hover:bg-gray-700 shadow flex items-center justify-center space-x-2">
            <span>{mostrarHistorialEquipos ? 'Ocultar' : 'Ver'} Historial General</span>
            <ChevronLeft size={20} style={{ transform: mostrarHistorialEquipos ? 'rotate(90deg)' : 'rotate(-90deg)', transition: 'transform 0.3s' }} />
          </button>
          {(mostrarHistorialEquipos || (busqueda && equiposHistoricosPorFecha.length > 0)) && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold text-gray-700 mb-3">Historial</h3>
              {historialDias.length === 0 ? <p className="text-center text-gray-400">Sin historial</p> : (
                <div className="space-y-4">
                  {(busqueda ? equiposHistoricosPorFecha : historialDias.slice().reverse()).map((dia, idx) => {
                    const equiposAMostrar = busqueda ? dia.equipos : dia.equipos;
                    if (equiposAMostrar.length === 0) return null;
                    return (
                      <div key={idx} className="border-l-4 border-gray-400 pl-4 py-2">
                        <h4 className="font-bold text-gray-800">{dia.fecha}</h4>
                        <div className="space-y-2">
                          {equiposAMostrar.map((eq, i) => (
                            <div key={i} className="bg-gray-50 p-3 rounded-lg border cursor-pointer" onClick={() => { setEquipoSeleccionado(eq); setVista('detalle'); }}>
                              <p className="font-medium">{eq.clienteNombre}</p>
                              <p className="text-sm text-gray-600">{eq.detalle}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // DETALLE VIEW
  if (vista === 'detalle' && equipoSeleccionado) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-blue-600 text-white p-4 shadow-lg">
          <div className="flex items-center space-x-3">
            <button onClick={() => setVista('equipos')}><ChevronLeft size={24} /></button>
            <h2 className="text-xl font-bold">Detalle del Equipo</h2>
          </div>
        </div>
        <div className="p-4 space-y-4">
          {equipoSeleccionado.foto && <div className="relative"><img src={equipoSeleccionado.foto} alt="Equipo" className="w-full h-64 object-cover rounded-lg shadow" /><button onClick={() => setImagenModalUrl(equipoSeleccionado.foto)} className="absolute bottom-3 right-3 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm">Ver imagen</button></div>}
          <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <h3 className="font-bold text-gray-700 border-b pb-2">Cliente</h3>
            <p className="font-medium">{equipoSeleccionado.clienteNombre} <span className="text-sm text-gray-500">({equipoSeleccionado.clienteCedula})</span></p>
            <p className="text-sm">{equipoSeleccionado.clienteContacto}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <h3 className="font-bold text-gray-700 border-b pb-2">Equipo</h3>
            <p className="font-medium">{equipoSeleccionado.detalle}</p>
            <p className="text-sm capitalize">Estado: {equipoSeleccionado.estado}</p>
            {equipoSeleccionado.tecnico && <p className="text-sm text-blue-600">Técnico: {equipoSeleccionado.tecnico}</p>}
            {equipoSeleccionado.riesgo && <p className="text-red-600 font-bold">Riesgo en Reparación</p>}
            {equipoSeleccionado.credito && <p className={equipoSeleccionado.deudaSaldada ? 'text-green-600' : 'text-yellow-600'}>{equipoSeleccionado.deudaSaldada ? 'Deuda Saldada' : 'Pago a Crédito'}</p>}
          </div>
          <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <div className="flex justify-between"><p>Precio:</p><p className="font-bold">${equipoSeleccionado.precioReparacion}</p></div>
            <div className="flex justify-between"><p>Costo:</p><p className="font-bold">${equipoSeleccionado.costoRepuesto}</p></div>
          </div>
        </div>
      </div>
    );
  }

  // CLIENTES VIEW
  if (vista === 'clientes') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-blue-600 text-white p-4 shadow-lg">
          <div className="flex items-center space-x-3">
            <button onClick={() => setVista('dashboard')}><ChevronLeft size={24} /></button>
            <h2 className="text-xl font-bold">Clientes</h2>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <input type="text" placeholder="Buscar..." value={busquedaClientes} onChange={(e) => setBusquedaClientes(e.target.value)} className="w-full p-3 border rounded-lg" />
          {clientesFiltrados.map(cliente => {
            const equiposEnCredito = equipos.filter(e => e.clienteId === cliente.id && e.credito && !e.deudaSaldada);
            const totalDeuda = equiposEnCredito.reduce((sum, e) => sum + parseFloat(e.precioReparacion || 0), 0);
            return (
              <div key={cliente.id} className="bg-white rounded-lg shadow">
                <div className="p-4 cursor-pointer hover:bg-gray-50" onClick={() => { setClienteEditando(cliente); setVista('editarCliente'); }}>
                  <div className="flex justify-between">
                    <div><h3 className="font-bold">{cliente.nombre}</h3><p className="text-sm">{cliente.contacto}</p></div>
                    {totalDeuda > 0 && <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-bold">${totalDeuda.toFixed(2)}</span>}
                  </div>
                </div>
                {equiposEnCredito.length > 0 && (
                  <div className="bg-yellow-50 p-3 border-t border-yellow-200">
                    <p className="text-xs font-bold text-yellow-800 mb-2">Créditos Pendientes:</p>
                    {equiposEnCredito.map(eq => (
                      <div key={eq.id} className="bg-white p-2 rounded border mb-2">
                        <p className="text-sm">{eq.detalle} - ${eq.precioReparacion}</p>
                        <button onClick={() => marcarDeudaSaldada(eq.id)} className="mt-2 w-full bg-green-600 text-white text-xs py-1 rounded">Marcar Pagado</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    );
  }

  // EDITAR CLIENTE
  if (vista === 'editarCliente' && clienteEditando) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-blue-600 text-white p-4 shadow-lg">
          <div className="flex items-center space-x-3"><button onClick={() => { setClienteEditando(null); setVista('clientes'); }}><ChevronLeft size={24} /></button><h2 className="text-xl font-bold">Editar Cliente</h2></div>
        </div>
        <div className="p-4 space-y-4">
          <input className="w-full p-3 border rounded" value={nombreEdit} onChange={e => setNombreEdit(e.target.value)} placeholder="Nombre" />
          <input className="w-full p-3 border rounded" value={cedulaEdit} onChange={e => setCedulaEdit(e.target.value)} placeholder="Cédula" />
          <input className="w-full p-3 border rounded" value={contactoEdit} onChange={e => setContactoEdit(e.target.value)} placeholder="Contacto" />
          <button onClick={guardarEdicionCliente} className="w-full bg-blue-600 text-white p-4 rounded font-bold">Guardar Cambios</button>
          <button onClick={() => eliminarCliente(clienteEditando.id)} className="w-full bg-red-600 text-white p-4 rounded font-bold">Eliminar Cliente</button>
        </div>
      </div>
    );
  }

  // TÉCNICOS VIEW
  if (vista === 'frecuentes') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-orange-600 text-white p-4 shadow-lg">
          <div className="flex items-center space-x-3"><button onClick={() => setVista('dashboard')}><ChevronLeft size={24} /></button><h2 className="text-xl font-bold">Técnicos</h2></div>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-white p-4 rounded shadow space-y-3">
            <h3 className="font-bold">Nuevo Técnico</h3>
            <input className="w-full p-2 border rounded" placeholder="Nombre" value={nuevoClienteFrecuente.nombre} onChange={e => setNuevoClienteFrecuente({ ...nuevoClienteFrecuente, nombre: e.target.value })} />
            <input className="w-full p-2 border rounded" placeholder="Cédula" value={nuevoClienteFrecuente.cedula} onChange={e => setNuevoClienteFrecuente({ ...nuevoClienteFrecuente, cedula: e.target.value })} />
            <input className="w-full p-2 border rounded" placeholder="Contacto" value={nuevoClienteFrecuente.contacto} onChange={e => setNuevoClienteFrecuente({ ...nuevoClienteFrecuente, contacto: e.target.value })} />
            <button onClick={guardarClienteFrecuente} className="w-full bg-orange-600 text-white p-2 rounded">Agregar</button>
          </div>
          <div className="space-y-2">
            {clientes.map(c => (
              <div key={c.id} className="bg-white p-3 rounded shadow flex justify-between items-center">
                <div><p className="font-bold">{c.nombre}</p><p className="text-xs">{c.cedula}</p></div>
                <button onClick={() => toggleClienteFrecuente(c)} className={`w-6 h-6 rounded border ${c.esFrecuente ? 'bg-orange-600' : ''}`}>{c.esFrecuente && '✓'}</button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // CONTABILIDAD VIEW
  if (vista === 'contabilidad') {
    const equiposConCostos = equipos.filter(e => e.precioReparacion || e.costoRepuesto);
    const equiposContables = equiposConCostos.filter(e => !e.credito || e.deudaSaldada);
    const totalIngresos = equiposContables.reduce((sum, e) => sum + parseFloat(e.precioReparacion || 0), 0);
    const totalRepuestos = equiposContables.reduce((sum, e) => sum + parseFloat(e.costoRepuesto || 0), 0);
    const gananciaTotal = totalIngresos - totalRepuestos;

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-green-600 text-white p-4 shadow-lg">
          <div className="flex items-center space-x-3"><button onClick={() => setVista('dashboard')}><ChevronLeft size={24} /></button><h2 className="text-xl font-bold">Contabilidad</h2></div>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-green-500 text-white p-6 rounded shadow"><p>Ganancia Día</p><p className="text-4xl font-bold">${gananciaTotal.toFixed(2)}</p></div>
          <button onClick={cerrarDia} className="w-full bg-orange-600 text-white p-4 rounded font-bold shadow">CERRAR DÍA</button>
          <div className="bg-white rounded shadow p-4">
            <h3 className="font-bold mb-3">Reparaciones Hoy</h3>
            {equiposConCostos.map(eq => (
              <div key={eq.id} className="border-b py-2 cursor-pointer" onClick={() => { setEquipoSeleccionado(eq); setVista('editarCostos'); }}>
                <div className="flex justify-between">
                  <div><p>{eq.clienteNombre}</p><p className="text-xs text-gray-500">{eq.detalle}</p></div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">${(parseFloat(eq.precioReparacion || 0) - parseFloat(eq.costoRepuesto || 0)).toFixed(2)}</p>
                    {eq.credito && !eq.deudaSaldada && <button onClick={(e) => { e.stopPropagation(); marcarDeudaSaldada(eq.id); }} className="text-xs bg-green-600 text-white px-2 py-1 rounded">Marcar Pagado</button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {historialDias.length > 0 && (
            <div className="bg-white rounded shadow p-4">
              <h3 className="font-bold mb-3">Historial Días</h3>
              {historialDias.slice().reverse().map((dia, idx) => (
                <div key={idx} className="border-l-4 border-gray-400 pl-3 py-2 mb-2 cursor-pointer hover:bg-gray-50" onClick={() => { setDiaSeleccionado(dia); setVista('detalleDia'); }}>
                  <div className="flex justify-between"><p className="font-bold">{dia.fecha}</p><p className="font-bold text-green-600">${dia.gananciaTotal.toFixed(2)}</p></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // DETALLE DÍA PASADO
  if (vista === 'detalleDia' && diaSeleccionado) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gray-700 text-white p-4"><div className="flex items-center space-x-3"><button onClick={() => setVista('contabilidad')}><ChevronLeft size={24} /></button><h2 className="text-xl font-bold">{diaSeleccionado.fecha}</h2></div></div>
        <div className="p-4 space-y-4">
          <div className="bg-white p-4 rounded shadow"><p>Ganancia Total</p><p className="text-3xl font-bold text-green-600">${diaSeleccionado.gananciaTotal.toFixed(2)}</p></div>
          <div className="bg-white p-4 rounded shadow space-y-2">
            {diaSeleccionado.equipos.map((eq, i) => (
              <div key={i} className="border-b py-2"><p className="font-bold">{eq.clienteNombre}</p><p className="text-sm">{eq.detalle}</p><p className="text-right font-bold text-green-600">${(parseFloat(eq.precioReparacion || 0) - parseFloat(eq.costoRepuesto || 0)).toFixed(2)}</p></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // EDITAR COSTOS
  if (vista === 'editarCostos' && equipoSeleccionado) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-green-600 text-white p-4"><div className="flex items-center space-x-3"><button onClick={() => setVista('contabilidad')}><ChevronLeft size={24} /></button><h2 className="text-xl font-bold">Editar Costos</h2></div></div>
        <div className="p-4 space-y-4">
          <div className="bg-white p-4 rounded shadow"><p className="font-bold">{equipoSeleccionado.clienteNombre}</p><p>{equipoSeleccionado.detalle}</p></div>
          <div className="bg-white p-4 rounded shadow space-y-3">
            <input type="number" className="w-full p-2 border rounded" placeholder="Precio" value={precioTemp} onChange={e => setPrecioTemp(e.target.value)} />
            <input type="number" className="w-full p-2 border rounded" placeholder="Costo Repuesto" value={costoTemp} onChange={e => setCostoTemp(e.target.value)} />
            <button onClick={guardarCambiosCostos} className="w-full bg-green-600 text-white p-4 rounded font-bold">Guardar</button>
          </div>
        </div>
      </div>
    );
  }

  // NUEVO EQUIPO FORM
  if (vista === 'nuevo') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-blue-600 text-white p-4"><div className="flex items-center space-x-3"><button onClick={() => setVista('dashboard')}><ChevronLeft size={24} /></button><h2 className="text-xl font-bold">Nuevo Equipo</h2></div></div>
        <div className="p-4 space-y-4">
          <div className="bg-white p-4 rounded shadow space-y-3">
            <h3 className="font-bold">Cliente</h3>
            {clientesFrecuentes.length > 0 && (
              <select className="w-full p-2 border rounded bg-purple-50" value={nuevoEquipo.clienteId} onChange={(e) => { const c = clientesFrecuentes.find(cl => cl.id === e.target.value); if (c) seleccionarCliente(c); }}>
                <option value="">Seleccionar Técnico...</option>
                {clientesFrecuentes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            )}
            <input className="w-full p-2 border rounded" placeholder="Nombre *" value={nuevoEquipo.clienteNombre} onChange={e => setNuevoEquipo({ ...nuevoEquipo, clienteNombre: e.target.value, clienteId: '' })} />
            {!nuevoEquipo.clienteId && (
              <>
                <input className="w-full p-2 border rounded" placeholder="Cédula" value={nuevoEquipo.clienteCedula} onChange={e => setNuevoEquipo({ ...nuevoEquipo, clienteCedula: e.target.value })} />
                <input className="w-full p-2 border rounded" placeholder="Contacto" value={nuevoEquipo.clienteContacto} onChange={e => setNuevoEquipo({ ...nuevoEquipo, clienteContacto: e.target.value })} />
              </>
            )}
          </div>

          <div className="bg-white p-4 rounded shadow space-y-3">
            <h3 className="font-bold">Equipo</h3>
            <input type="file" accept="image/*" onChange={handleImagenCaptura} className="w-full p-2 border rounded" />
            {nuevoEquipo.foto && <img src={nuevoEquipo.foto} className="w-full h-32 object-cover rounded" />}
            <textarea className="w-full p-2 border rounded" rows="3" placeholder="Detalle *" value={nuevoEquipo.detalle} onChange={e => setNuevoEquipo({ ...nuevoEquipo, detalle: e.target.value })} />
            <select className="w-full p-2 border rounded" value={nuevoEquipo.estado} onChange={e => setNuevoEquipo({ ...nuevoEquipo, estado: e.target.value })}>
              <option value="apagado">Apagado</option>
              <option value="detalle">Con detalle</option>
            </select>
            <label className="flex items-center space-x-2 bg-red-50 p-2 rounded"><input type="checkbox" checked={nuevoEquipo.riesgo} onChange={e => setNuevoEquipo({ ...nuevoEquipo, riesgo: e.target.checked })} /><span>Riesgo</span></label>
            <label className="flex items-center space-x-2 bg-yellow-50 p-2 rounded border border-yellow-300"><input type="checkbox" checked={nuevoEquipo.credito} onChange={e => setNuevoEquipo({ ...nuevoEquipo, credito: e.target.checked })} /><span>A Crédito</span></label>
            <select className="w-full p-2 border rounded" value={nuevoEquipo.tecnico} onChange={e => setNuevoEquipo({ ...nuevoEquipo, tecnico: e.target.value })}>
              <option value="">Técnico...</option>
              <option value="Don Jorge">Don Jorge</option>
              <option value="David">David</option>
            </select>
          </div>

          <div className="bg-white p-4 rounded shadow space-y-3">
            <h3 className="font-bold">Costos (Opcional)</h3>
            <input type="number" className="w-full p-2 border rounded" placeholder="Precio" value={nuevoEquipo.precioReparacion} onChange={e => setNuevoEquipo({ ...nuevoEquipo, precioReparacion: e.target.value })} />
            <input type="number" className="w-full p-2 border rounded" placeholder="Costo Repuesto" value={nuevoEquipo.costoRepuesto} onChange={e => setNuevoEquipo({ ...nuevoEquipo, costoRepuesto: e.target.value })} />
          </div>

          <button onClick={guardarEquipo} disabled={cargando} className="w-full bg-blue-600 text-white p-4 rounded font-bold shadow">{cargando ? 'Guardando...' : 'Guardar Equipo'}</button>
        </div>
      </div>
    );
  }

  return null;
};

export default App;